<template>
  <div 
    class="horizontal-scroll-viewport" 
    ref="scrollViewport" 
    @mouseenter="pauseAutoScroll" 
    @mouseleave="resumeAutoScroll"
  >
    <div 
      class="horizontal-scroll-track" 
      :style="{ transform: `translateX(${scrollPosition}px)`, gap: gap + 'px' }"
    >
      <div 
        v-for="(item, idx) in displayedItems" 
        :key="idx" 
        class="scroll-item"
        :style="{ flex: '0 0 auto' }"
      >
        <slot 
          :item="item" 
          :index="idx % items.length" 
          :isClone="idx >= items.length"
        ></slot>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'HorizontalScroll',
  props: {
    /**
     * 滚动项数组
     */
    items: {
      type: Array,
      required: true
    },
    /**
     * 每项的宽度（像素），如果不设置则根据内容自适应
     */
    itemWidth: {
      type: Number,
      default: 0
    },
    /**
     * 项之间的间隙（像素）
     */
    gap: {
      type: Number,
      default: 30
    },
    /**
     * 滚动速度（像素/帧）
     */
    speed: {
      type: Number,
      default: 1
    },
    /**
     * 是否自动播放
     */
    autoPlay: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      displayedItems: [],
      scrollPosition: 0,
      animationFrameId: null,
      isPaused: false,
      itemWidthCalculated: 0
    };
  },
  mounted() {
    this.initializeItems();
    if (this.autoPlay) {
      this.startAutoScroll();
    }
  },
  beforeDestroy() {
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
    }
  },
  watch: {
    items: {
      handler() {
        this.initializeItems();
      },
      deep: true
    }
  },
  methods: {
    /**
     * 初始化显示项（复制数组以实现无缝滚动）
     */
    initializeItems() {
      // 复制两份数据以实现无缝滚动
      this.displayedItems = [...this.items, ...this.items];
    },
    
    /**
     * 开始自动滚动动画
     */
    startAutoScroll() {
      const animate = () => {
        // 只有在自动播放且未暂停时才滚动
        if (this.autoPlay && !this.isPaused) {
          this.scrollPosition -= this.speed;
          
          // 计算单组的宽度
          const singleSetWidth = this.calculateSingleSetWidth();
          
          // 当滚动超过第一组数据宽度时，重置位置以实现无缝循环
          if (singleSetWidth > 0 && this.scrollPosition <= -singleSetWidth) {
            this.scrollPosition = 0;
          }
          // 如果位置变成正数，也要重置到0（处理边界情况）
          else if (this.scrollPosition > 0) {
            this.scrollPosition = 0;
          }
        }
        
        this.animationFrameId = requestAnimationFrame(animate);
      };
      
      this.animationFrameId = requestAnimationFrame(animate);
    },
    
    /**
     * 暂停自动滚动
     */
    pauseAutoScroll() {
      this.isPaused = true;
    },
    
    /**
     * 恢复自动滚动
     */
    resumeAutoScroll() {
      this.isPaused = false;
    },
    
    /**
     * 计算单组数据的总宽度
     */
    calculateSingleSetWidth() {
      if (this.itemWidth > 0) {
        return this.items.length * (this.itemWidth + this.gap);
      } else {
        // 如果没有设置itemWidth，需要动态计算
        if (this.$refs.scrollViewport && this.displayedItems.length > 0) {
          // 获取第一个项目的宽度作为参考
          const firstItem = this.$refs.scrollViewport.querySelector('.scroll-item');
          if (firstItem) {
            this.itemWidthCalculated = firstItem.offsetWidth;
            return this.items.length * (this.itemWidthCalculated + this.gap);
          }
        }
        return 0;
      }
    },
    
    /**
     * 手动设置滚动位置
     */
    scrollTo(position) {
      this.scrollPosition = position;
    },
    
    /**
     * 获取当前滚动位置
     */
    getScrollPosition() {
      return this.scrollPosition;
    }
  }
};
</script>

<style lang="less" scoped>
.horizontal-scroll-viewport {
  width: 100%;
  overflow: hidden;
  position: relative;
}

.horizontal-scroll-track {
  display: flex;
  align-items: center;
  width: max-content;
}

.scroll-item {
  flex: 0 0 auto;
}
</style>